import React, { useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import StatusChip from "./StatusChip"
import Avatar from "./Avatar"
import ProgressBar from "./ProgressBar"
import AssignablePicker from "./AssignablePicker"
import TaskPhotosModal from "./TaskPhotosModal"
import { fmt, dateFmt } from "../helpers/format"
import { usePanorama } from "../context/PanoramaContext"
import { patchTask } from "../api/tasks"

const Chevron = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
       className={`transition-transform ${open ? "rotate-90" : ""}`}>
    <path d="M9 18l6-6-6-6" />
  </svg>
)

const Connector = ({ depth }) => {
  if (depth === 0) return null
  return (
    <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 flex">
      {Array.from({ length: depth }).map((_, i) => (
        <span key={i} className="block" style={{ width: 28 }}>
          <span className="block h-full border-l border-slate-200 mx-auto" style={{ width: 1 }} />
        </span>
      ))}
    </span>
  )
}

const AssignableCell = ({ task, projectId }) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const { dispatch } = usePanorama()

  const applyResponse = res => {
    if (!res?.task) return
    dispatch({ type: "PATCH_TASK", id: task.id, patch: res.task })
    if (res.descendants?.length) {
      dispatch({
        type:    "PATCH_TASKS",
        patches: res.descendants.map(d => ({ id: d.id, patch: d })),
      })
    }
  }

  const apply = ({ kind, id }) => {
    const type = kind === "user" ? "User" : "Supplier"
    patchTask(projectId, task.id, { assignable_type: type, assignable_id: id }).then(applyResponse)
  }
  const clear = () => {
    patchTask(projectId, task.id, { assignable_type: "", assignable_id: "" })
      .then(res => {
        if (!res?.task) return
        dispatch({ type: "PATCH_TASK", id: task.id, patch: { ...res.task, assignable: null, subcontracted: false } })
        if (res.descendants?.length) {
          dispatch({
            type:    "PATCH_TASKS",
            patches: res.descendants.map(d => ({ id: d.id, patch: d })),
          })
        }
      })
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 min-w-0 text-left px-1.5 py-1 -mx-1.5 rounded hover:bg-slate-100"
      >
        {!task.assignable ? (
          <span className="text-[12px] text-slate-400 italic">{I18n.t("projects.wbs.assign")}</span>
        ) : task.assignable.kind === "supplier" ? (
          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[11.5px] font-medium">
            <FontAwesomeIcon icon="truck" className="w-3 h-3" />
            <span className="truncate max-w-[140px]">{task.assignable.name}</span>
          </span>
        ) : (
          <>
            <Avatar name={task.assignable.name} size="sm" ring={false} />
            <span className="text-[12px] text-slate-700 truncate">{task.assignable.name}</span>
          </>
        )}
      </button>
      {open && (
        <AssignablePicker
          projectId={projectId}
          anchorEl={anchorRef.current}
          value={task.assignable}
          onPick={apply}
          onClear={clear}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

const DateCell = ({ task, parent, field, projectId }) => {
  const value = task[field]
  const inputRef = useRef(null)
  const { dispatch } = usePanorama()

  const min = field === "start_date"
    ? parent?.start_date || undefined
    : task.start_date    || parent?.start_date || undefined
  const max = field === "end_date"
    ? parent?.end_date   || undefined
    : task.end_date      || parent?.end_date   || undefined

  const onChange = e => {
    const next = e.target.value || null
    dispatch({ type: "PATCH_TASK", id: task.id, patch: { [field]: next } })
    patchTask(projectId, task.id, { [field]: next }).then(res => {
      if (!res?.task) return
      dispatch({ type: "PATCH_TASK", id: task.id, patch: res.task })
      if (res.descendants?.length) {
        dispatch({
          type:    "PATCH_TASKS",
          patches: res.descendants.map(d => ({ id: d.id, patch: d })),
        })
      }
    })
  }

  const openPicker = () => {
    const el = inputRef.current
    if (!el) return
    if (typeof el.showPicker === "function") el.showPicker()
    else el.focus()
  }

  return (
    <div
      onClick={openPicker}
      className="relative inline-flex items-center text-[12px] tabular-nums text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-1.5 py-1 -mx-1.5 cursor-pointer"
      title={parent ? I18n.t("projects.wbs.parent_limit", { start: parent.start_date || "—", end: parent.end_date || "—" }) : undefined}
    >
      <span>{value ? dateFmt(value) : <span className="text-slate-400 italic">{I18n.t("projects.wbs.add_date")}</span>}</span>
      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        min={min}
        max={max}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  )
}

const WbsRow = ({ task, project, parent = null, depth = 0, collapsed, onToggle }) => {
  const hasChildren = task.children_count > 0 && task.children?.length > 0
  const open = !collapsed.has(task.id)
  const { dispatch } = usePanorama()

  const isOverInvoiced  = task.percentage_invoices > task.completion + 5
  const isLeaf          = !hasChildren
  const photosCount     = task.photos_count || 0
  const [photosOpen, setPhotosOpen] = useState(false)

  const onPhotosCountChange = (taskId, nextCount) => {
    dispatch({ type: "PATCH_TASK", id: taskId, patch: { photos_count: nextCount } })
  }

  return (
    <>
      <div className="grid grid-cols-[minmax(220px,2fr)_110px_180px_110px_110px_120px_60px] items-center gap-3 px-3 py-2 hover:bg-slate-50/60 group">
        <div className="relative flex items-center min-w-0" style={{ paddingLeft: depth * 28 }}>
          <Connector depth={depth} />
          <button
            type="button"
            onClick={() => hasChildren && onToggle(task.id)}
            className={`shrink-0 inline-grid place-items-center w-5 h-5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 ${hasChildren ? "" : "invisible"}`}
            aria-label={open ? I18n.t("projects.wbs.collapse") : I18n.t("projects.wbs.expand")}
          >
            <Chevron open={open} />
          </button>
          <span className="ml-1.5 truncate text-[13px] text-slate-900 font-medium">
            {task.title}
          </span>
        </div>

        <div>
          <StatusChip status={task.status} label={task.status_label} dot />
        </div>

        <div className="min-w-0">
          <AssignableCell task={task} projectId={project.id} />
        </div>

        <div><DateCell task={task} parent={parent} field="start_date" projectId={project.id} /></div>
        <div><DateCell task={task} parent={parent} field="end_date"   projectId={project.id} /></div>

        <div className="flex flex-col gap-1">
          <ProgressBar value={task.completion}          tone={isOverInvoiced ? "amber" : "blue"} height={5} />
          <ProgressBar value={task.percentage_invoices} tone="emerald" height={4} />
          <div className="flex justify-between text-[10.5px] text-slate-500 tabular-nums">
            <span>av. {Math.round(task.completion || 0)}%</span>
            <span>fact. {Math.round(task.percentage_invoices || 0)}%</span>
          </div>
        </div>

        <div className="text-right flex items-center justify-end gap-1">
          {isLeaf && (
            <button
              type      = "button"
              onClick   = {() => setPhotosOpen(true)}
              title     = {I18n.t("projects.task_photos.cta_title", { count: photosCount })}
              className = {`inline-flex items-center gap-1 px-2 py-1 rounded text-[11.5px] transition-colors ${
                photosCount > 0
                  ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-700 opacity-0 group-hover:opacity-100"
              }`}
            >
              <FontAwesomeIcon icon="camera" className="w-3 h-3" />
              {photosCount > 0 && <span className="tabular-nums font-medium">{photosCount}</span>}
            </button>
          )}
        </div>
      </div>

      {isLeaf && photosOpen && (
        <TaskPhotosModal
          isOpen          = {photosOpen}
          projectId       = {project.id}
          task            = {task}
          onClose         = {() => setPhotosOpen(false)}
          onCountChange   = {onPhotosCountChange}
        />
      )}

      {hasChildren && open && task.children.map(child => (
        <WbsRow key={child.id} task={child} project={project} parent={task} depth={depth + 1} collapsed={collapsed} onToggle={onToggle} />
      ))}
    </>
  )
}

export default WbsRow
